// onboarding.jsx — 4-step first-run flow after sign-up
// Captures profile (display name + sex + referral + hospital + role), cycle
// dates. Writes to public.profiles via DB.upsertProfile and flips onboarded=true.

// Hooks are called as `React.useX(...)` member calls so react-hooks/
// rules-of-hooks can see them (it only recognises `/^use[A-Z]/` and
// `React.useX`). Do not reintroduce destructured short aliases.

// Pull whatever name a third-party provider (Apple / OAuth) already handed us
// so we can pre-fill the display-name field instead of asking again. Apple
// only supplies this on the first authorisation, and Supabase surfaces it in
// user_metadata; we also accept a name we previously persisted to the profile.
function providerDisplayName(session) {
  const m = session?.user?.user_metadata || {};
  const candidate =
    m.display_name || m.full_name || m.name ||
    [m.given_name, m.family_name].filter(Boolean).join(' ') ||
    [m.givenName, m.familyName].filter(Boolean).join(' ');
  return (candidate || '').trim();
}

// Build the initial form from any data already on the profile, falling back to
// provider metadata then sensible defaults. This matters for two re-entry
// paths: (1) an Apple user whose name db.js persisted at sign-in but who never
// finished onboarding — user_metadata no longer carries the name, but the
// profile row does; (2) a legacy user tapping "Complete profile" whose
// workplace is stored as free text predating the dropdown — we best-effort
// match it back to a key + sector so the dropdown shows their hospital
// selected rather than blank (which would invite an accidental overwrite).
// NCHK minimum CNE points per 3-year cycle, keyed by role: Registered Nurse
// must earn 45, Enrolled Nurse 30. Roles not listed here (Advanced Practice,
// Nurse Educator, Other) have no statutory default — they keep whatever value
// the field already holds and are never auto-filled. Defined up here because
// both initialForm() and the role picker derive the default from it.
// NCHK point minimums by role: a Registered Nurse must earn 45, an Enrolled
// Nurse 30 — per 3-year cycle, regardless of specialty (the specialty only
// changes the CNE *content* coverage, not the count). So the Psychiatric
// variants carry the same tier as their General counterparts.
const ROLE_REQUIRED_POINTS = {
  'Registered Nurse': 45,
  'Enrolled Nurse': 30,
  'Registered Nurse (Psychiatric)': 45,
  'Enrolled Nurse (Psychiatric)': 30,
};

function initialForm(session, profile) {
  const email = session?.user?.email || '';
  const byKey = window.HK_HOSPITALS_BY_KEY || {};
  let hospital_key = profile?.hospital_key || '';
  let hospital_sector = profile?.hospital_sector || '';
  let hospital_name = '';
  // A 'text' sector (NGO / clinic / home / school / other) stores a free-text
  // organisation name, so seed the text input and NEVER run matchHospital over
  // it — that back-match is substring-based in both directions and would
  // resolve an NGO named "Caritas" onto 明愛醫院, overwriting the user's answer.
  if ((window.sectorKind ? window.sectorKind(hospital_sector) : null) === 'text') {
    hospital_key = '';
    hospital_name = profile?.hospital_zh || profile?.hospital_en || '';
  } else if (!hospital_key && (profile?.hospital_en || profile?.hospital_zh)) {
    const m = window.matchHospital ? window.matchHospital(profile.hospital_en || profile.hospital_zh) : null;
    if (m) { hospital_key = m.key; hospital_sector = m.sector; }
  } else if (hospital_key && byKey[hospital_key]) {
    hospital_sector = byKey[hospital_key].sector;
  }
  const displayName =
    (profile?.display_name || profile?.name_en || profile?.name_zh || '').trim()
    || providerDisplayName(session);
  const roleEn = profile?.role_en || 'Registered Nurse';
  const roleZh = profile?.role_zh || '註冊護士';
  // A dual-registered nurse's optional second certificate lives in the license2
  // jsonb column; flatten it into license2_* form keys so the shared capture can
  // edit it, and reassemble on save (license2FromForm). Present only when it
  // carries a usable cycle_start — a half-filled blob is treated as absent.
  const l2 = (profile?.license2 && typeof profile.license2 === 'object') ? profile.license2 : null;
  const hasL2 = !!(l2 && l2.cycle_start);
  return {
    display_name: displayName,
    // Official names are document identity, not the in-app display name.
    // Keep them as separate form values so changing a nickname never rewrites
    // the name printed on NCHK Annex B.
    official_name_en: profile?.name_en || '',
    official_name_zh: profile?.name_zh || '',
    sex: profile?.sex || '',
    referral_source: profile?.referral_source || '',
    hospital_sector: hospital_sector || 'public',
    hospital_key,
    // Free-text organisation name — used only by 'text' sectors. Held raw
    // while typing; sanitizeWorkplaceName normalises it on save.
    hospital_name,
    role_en: roleEn,
    role_zh: roleZh,
    license_no: profile?.license_no || '',
    // Never disguise an unanswered cycle as valid data. New users start with
    // honest blanks; returning users keep whatever they previously saved.
    cycle_start: profile?.cycle_start || '',
    renewal_date: profile?.renewal_date || '',
    // A returning user's saved value always wins; otherwise seed from the
    // role's statutory minimum (RN 45 / EN 30), falling back to 45. This is
    // the *only* implicit fill — after this, points change solely from a
    // manual edit or an explicit role switch (see the role picker).
    required_points: profile?.required_points || ROLE_REQUIRED_POINTS[roleEn] || 45,
    baseline_points: 0,
    // First-PC applicants are exempt from CNE until their SECOND certificate
    // (NCHK: only nurses "not applying for the first time" are bound). Gates the
    // mandatory framing — see licenseIsMandatory / profileLicenses in data.js.
    first_pc: !!profile?.first_pc,
    // Dual registration — an optional SECOND practising certificate, captured as
    // a second ROLE (which encodes the register part, e.g. 精神科) + its own
    // cycle. Flattened into license2_* form keys so the pickers can edit it and
    // reassembled on save (license2FromForm). Collapsed + empty for the
    // single-licence majority; present only when license2 has a usable cycle.
    has_license2: hasL2,
    license2_role_en: (l2 && l2.role_en) || '',
    license2_role_zh: (l2 && l2.role_zh) || '',
    license2_license_no: (l2 && l2.license_no) || '',
    license2_cycle_start: (l2 && l2.cycle_start) || '',
    license2_renewal_date: (l2 && l2.renewal_date) || '',
    license2_required_points: (l2 && l2.required_points != null) ? l2.required_points : '',
    license2_baseline_points: (l2 && l2.baseline_points != null) ? l2.baseline_points : '',
  };
}

// Reassemble the flat license2_* form keys into the jsonb blob the profile
// stores — or null when the user isn't holding a second certificate (or hasn't
// entered its start date yet). Shared by onboarding's finish() and Edit
// Profile's save so both write an identically-shaped license2.
function license2FromForm(form) {
  if (!form.has_license2 || !form.license2_cycle_start) return null;
  return {
    role_en: form.license2_role_en || 'Registered Nurse',
    role_zh: form.license2_role_zh || '註冊護士',
    license_no: (form.license2_license_no || '').trim() || null,
    cycle_start: form.license2_cycle_start,
    renewal_date: form.license2_renewal_date || pcEndFromStart(form.license2_cycle_start),
    required_points: Number(form.license2_required_points) || 45,
    baseline_points: Number(form.license2_baseline_points) || 0,
  };
}

function OnboardingFlow({ session, profile, lang, setLang, onDone, onSkip }) {
  const userId = session.user.id;
  const email  = session.user.email;
  const [step, setStep] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  // What happens the moment setup finishes: 'record' opens the AI scan,
  // 'browse' lands on Discover, 'later' does nothing. Defaults to 'record'
  // because the pre-selected card is what a nurse who just taps 完成設定 gets,
  // and that default is a meaningful share of first-record activation.
  const [firstAction, setFirstAction] = React.useState('record');
  const [form, setForm] = React.useState(() => initialForm(session, profile));
  // Breadcrumbs record the furthest step reached, never a back-navigation.
  // This is intentionally only a best-effort measurement path: onboarding must
  // still work when the network is unavailable.
  const maxBreadcrumbStep = React.useRef(0);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // PointsStep (baseline_points) removed per UX request: cycle progress
  // is derived entirely from records the user adds one by one. The
  // baseline_points column still exists in the schema (see migration)
  // but stays at 0 — its default — for every newly-onboarded user.
  // Existing rows with a non-zero baseline are unaffected.
  const steps = [
    { id: 'welcome', title: { en: 'Welcome', zh: '歡迎' } },
    { id: 'profile', title: { en: 'You',     zh: '你' } },
    { id: 'cycle',   title: { en: 'Cycle',   zh: '週期' } },
    { id: 'done',    title: { en: 'Set',     zh: '完成' } },
    { id: 'scan',    title: { en: 'First record', zh: '第一個紀錄' } },
  ];
  const progress = (step / (steps.length - 1)) * 100;

  const next = () => {
    const nextStep = Math.min(step + 1, steps.length - 1);
    if (nextStep > step) {
      if (nextStep > maxBreadcrumbStep.current) {
        maxBreadcrumbStep.current = nextStep;
        Promise.resolve(DB.setPrefs(userId, {
          onboarding: { step: steps[nextStep].id, at: new Date().toISOString() },
        })).catch(() => {});
      }
      setStep(nextStep);
    }
  };
  const back = () => setStep(s => Math.max(s - 1, 0));

  const finish = async (skipCycle = false) => {
    if (busy) return; // guard against a double-tap landing two upserts
    setBusy(true);
    const displayName = (form.display_name || '').trim() || email.split('@')[0];
    const shortName = displayName.split(' ')[0];
    // Merge the language preference into the profile so the choice is recorded
    // server-side and follows the user across devices.
    const existing = await DB.getProfile(userId).catch(() => null);
    const finishedAt = new Date().toISOString();
    const mergedPrefs = {
      ...(existing?.prefs || {}),
      lang,
      onboarding: {
        step: skipCycle ? (steps[step]?.id || 'cycle') : steps[steps.length - 1].id,
        at: finishedAt,
        // Which path they picked out of setup. 'browse' was added as a real
        // alternative to the dead-end skip, and the only way to know whether it
        // converts as well as 'record' is to record the choice at the moment it
        // is made — this cannot be reconstructed later. Whitelisted rather than
        // written straight from state so the funnel column can only ever hold
        // one of three known values.
        first_action: ['record', 'browse', 'later'].includes(firstAction) ? firstAction : 'later',
      },
    };
    const license2 = license2FromForm(form);
    const patch = {
      display_name: displayName,
      // Official document identity is separate from the in-app display name.
      // Preserve any name captured previously (for example by the NCHK export
      // gate), but never promote a nickname into Annex B automatically.
      name_en: (form.official_name_en || '').trim() || null,
      name_zh: (form.official_name_zh || '').trim() || null,
      short_name_en: shortName,
      sex: form.sex || null,
      referral_source: form.referral_source || null,
      // Workplace columns come from ONE assembler shared with Edit Profile
      // (hospital key + labels for a hospital, sanitised free text otherwise).
      // An incomplete selection yields null → the keys are omitted and the
      // stored workplace is left untouched rather than blanked.
      ...(workplacePatch(form) || {}),
      role_en: form.role_en,
      role_zh: form.role_zh,
      cycle_start: skipCycle ? null : form.cycle_start,
      renewal_date: skipCycle ? null : form.renewal_date,
      required_points: Number(form.required_points) || 45,
      first_pc: !!form.first_pc, // first-PC exemption (CNE not yet mandatory)
      // Dual registration: the optional second certificate (its own role + cycle)
      // or null. Reassembled from the flat form keys so onboarding + Edit Profile
      // agree on the shape.
      license2,
      prefs: mergedPrefs,
      // baseline_points intentionally omitted — defaults to 0 server-side.
      // All CNE point accumulation comes from records added later.
      // An explicitly unknown expiry stays incomplete and is surfaced by the
      // existing Home "Complete profile" path instead of creating fake dates.
      onboarded: !skipCycle,
      // `updated_at` changes on every later profile edit, so it cannot measure
      // how long setup took. Keep this immutable completion timestamp instead.
      onboarded_at: skipCycle ? null : finishedAt,
    };
    await DB.upsertProfile(userId, patch);
    setBusy(false);
    if (skipCycle) onSkip?.();
    else onDone({ firstAction });
  };

  return (
    <div className="auth-shell">
      <div className="auth-top">
        <div className="auth-brand">
          <div className="mono muted-2" style={{ fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
            {lang === 'zh' ? '設定' : 'Setup'} · {step + 1}/{steps.length}
          </div>
        </div>
        <LangPill lang={lang} setLang={setLang} />
      </div>

      {/* Progress bar */}
      <div style={{ padding: '0 28px' }}>
        <div className="onb-bar"><div className="onb-bar-fill" style={{ width: `${progress}%` }} /></div>
      </div>

      <div className="auth-body">
        {/* key={step} remounts on each step change, so the .onb-step entrance
            replays — a quick, interruptible "confirm the switch" with no exit
            animation (exits are what make a wizard feel draggy). */}
        <div className="onb-step" key={step}>
          {step === 0 && <Welcome lang={lang} email={email} onNext={next} onSkip={onSkip} />}
          {step === 1 && <ProfileStep lang={lang} form={form} set={set} onBack={back} onNext={next} />}
          {step === 2 && <CycleStep   lang={lang} form={form} set={set} onBack={back} onNext={next}
                                    onSkipCycle={() => finish(true)} busy={busy} />}
          {step === 3 && <DoneStep    lang={lang} form={form} busy={busy} onBack={back} onFinish={next} />}
          {step === 4 && <FirstActionStep lang={lang} firstAction={firstAction} setFirstAction={setFirstAction}
            busy={busy} onBack={back} onFinish={finish} />}
        </div>
      </div>
    </div>
  );
}

function StepNav({ lang, onBack, onNext, nextLabel, disabled, busy }) {
  return (
    <div className="onb-nav">
      {onBack
        ? <button className="auth-secondary" onClick={onBack}>{lang === 'zh' ? '上一步' : 'Back'}</button>
        : <span />}
      <button className="auth-primary" onClick={onNext} disabled={disabled}
        aria-busy={busy || undefined}
        style={busy ? { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 } : undefined}>
        {busy ? <Spinner size={15} color="currentColor" /> : null}
        <span>{nextLabel || (lang === 'zh' ? '繼續' : 'Continue')}</span>
      </button>
    </div>
  );
}

function Welcome({ lang, email, onNext, onSkip }) {
  return (
    <>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '建立你的紀錄' : 'Build your record'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? '由你開始。' : 'Let\u2019s start with you.'}
      </h1>
      <p className="auth-sub">
        {lang === 'zh'
          ? `你剛以 ${email} 註冊。三個簡短步驟——個人資料、續期週期和第一個紀錄。`
          : `Signed up as ${email}. Three short steps — profile, renewal cycle, and your first record.`}
      </p>
      <div className="onb-list">
        <OnbBullet n="01" lang={lang} en="Your display name and workplace" zh="顯示名稱及工作單位" />
        <OnbBullet n="02" lang={lang} en="Your registration cycle dates"   zh="註冊週期日期" />
        <OnbBullet n="03" lang={lang} en="Optional first record — scan or browse" zh="可選：掃描證書或課程連結" />
      </div>
      <div className="onb-nav onb-nav--welcome">
        <button className="auth-primary" onClick={onNext}>
          {lang === 'zh' ? '開始' : 'Begin'}
        </button>
        {onSkip ? (
          <button
            type="button"
            className="auth-link"
            onClick={onSkip}
            style={{ marginTop: 12 }}
          >
            {lang === 'zh' ? '稍後再說' : 'Skip for now'}
          </button>
        ) : null}
      </div>
    </>
  );
}

function OnbBullet({ n, lang, en, zh }) {
  return (
    <div className="onb-bullet">
      <div className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.16em' }}>{n}</div>
      <div className="onb-bullet-t">{lang === 'zh' ? zh : en}</div>
    </div>
  );
}

const SEX_OPTIONS = [
  { id: 'male',               en: 'Male',              zh: '男' },
  { id: 'female',             en: 'Female',            zh: '女' },
  { id: 'prefer_not_to_say',  en: 'Prefer not to say', zh: '不願透露' },
];

const REFERRAL_OPTIONS = [
  { id: 'app_store', en: 'App Store',         zh: 'App Store' },
  { id: 'colleague', en: 'Colleague / friend',zh: '同事 / 朋友' },
  { id: 'search',    en: 'Search engine',     zh: '搜尋引擎' },
  { id: 'social',    en: 'Social media',      zh: '社交媒體' },
  { id: 'instagram', en: 'Instagram',         zh: 'Instagram' },
  { id: 'nchk',      en: 'NCHK / hospital',   zh: '護士管理局 / 醫院' },
  { id: 'other',     en: 'Other',             zh: '其他' },
];

// Single source of truth for the role picker — consumed by the onboarding
// ProfileStep AND the standalone Edit Profile page (screens.jsx) so the two
// can't drift. The order matters: it's also the display order in both UIs.
const ROLE_OPTIONS = [
  { en: 'Registered Nurse',                zh: '註冊護士' },
  { en: 'Enrolled Nurse',                  zh: '登記護士' },
  { en: 'Registered Nurse (Psychiatric)',  zh: '註冊護士（精神科）' },
  { en: 'Enrolled Nurse (Psychiatric)',    zh: '登記護士（精神科）' },
  { en: 'Advanced Practice',               zh: '進階護理' },
  { en: 'Nurse Educator',                  zh: '護理導師' },
  { en: 'Other',                           zh: '其他' },
];

// Sector chips + either a hospital <select> ('list' sectors) or a free-text
// organisation name ('text' sectors), sourced from window.WORKPLACE_SECTORS.
//
// Shared by onboarding's ProfileStep and Edit Profile, so the two capture the
// workplace identically. Switching sector clears the OTHER kind's value, so a
// half-migrated selection ("private" + an NGO name) can never be submitted; the
// caller assembles the columns through workplacePatch().
//
// Chips (not the old two-tab segmented control) because the list is now seven
// sectors and has to wrap — same affordance as the role/sex pickers.
function HospitalPicker({ lang, sector, hospitalKey, orgName, onSector, onHospital, onOrgName }) {
  const groups = window.HK_HOSPITALS || { public: [], private: [] };
  const sectors = window.WORKPLACE_SECTORS || [];
  const current = window.sectorById ? window.sectorById(sector) : null;
  const kind = current ? current.kind : null;
  const list = sector === 'private' ? groups.private : groups.public;
  return (
    <div className="onb-field">
      <div className="auth-label mono">{lang === 'zh' ? '工作單位' : 'Workplace'}</div>
      <div className="onb-roles" role="group" aria-label={lang === 'zh' ? '工作單位類型' : 'Workplace sector'}>
        {sectors.map(s => (
          <button key={s.id} type="button"
            aria-pressed={sector === s.id}
            className={'onb-chip' + (sector === s.id ? ' on' : '')}
            onClick={() => {
              // Only reset when the sector actually changes — re-tapping the
              // active sector must not wipe a valid selection.
              if (sector === s.id) return;
              onSector(s.id);
              onHospital('');
              if (onOrgName) onOrgName('');
            }}>
            {lang === 'zh' ? s.zh : s.en}
          </button>
        ))}
      </div>
      {kind === 'list' ? (
        <select
          className="auth-input onb-select"
          value={hospitalKey}
          onChange={e => onHospital(e.target.value)}
        >
          <option value="">{lang === 'zh' ? '選擇醫院…' : 'Select hospital…'}</option>
          {list.map(h => (
            <option key={h.key} value={h.key}>
              {lang === 'zh' ? (h.zh || h.en) : h.en}
            </option>
          ))}
        </select>
      ) : null}
      {kind === 'text' ? (
        <>
          {/* Optional on purpose: the SECTOR is what we actually need, and a
              required name here would rebuild the very gate that locked out
              non-hospital nurses. maxLength is a UX guard only — the stored
              value is normalised by sanitizeWorkplaceName on save. */}
          <input className="auth-input" type="text"
            value={orgName || ''}
            maxLength={window.WORKPLACE_NAME_MAX || 60}
            autoComplete="organization"
            onChange={e => onOrgName && onOrgName(e.target.value)}
            placeholder={lang === 'zh' ? current.phZh : current.phEn} />
          <div className="muted-2" style={{ fontSize: 11.5, marginTop: 6 }}>
            {lang === 'zh' ? '機構名稱可留空。' : 'The organisation name is optional.'}
          </div>
        </>
      ) : null}
    </div>
  );
}

// Assemble the profiles.hospital_* columns from the current form selection, or
// null when the selection is incomplete (a 'list' sector with no hospital
// chosen). Callers spread the result and OMIT the columns entirely on null, so
// an incomplete edit can never half-write a workplace over a good one.
//
// The sector is taken from the resolved hospital for 'list' sectors, so a stale
// chip ("private" + a public hospital's key) can't persist a contradiction.
function workplacePatch(form) {
  const sector = form.hospital_sector || '';
  const kind = window.sectorKind ? window.sectorKind(sector) : null;
  if (kind === 'list') {
    const hosp = (window.HK_HOSPITALS_BY_KEY || {})[form.hospital_key] || null;
    if (!hosp) return null;
    return {
      hospital_sector: hosp.sector,
      hospital_key: form.hospital_key,
      hospital_en: hosp.en,
      hospital_zh: hosp.zh || hosp.en,
    };
  }
  if (kind === 'text') {
    const name = window.sanitizeWorkplaceName
      ? window.sanitizeWorkplaceName(form.hospital_name)
      : '';
    return {
      hospital_sector: sector,
      hospital_key: null,
      hospital_en: name || null,
      hospital_zh: name || null,
    };
  }
  return null;
}

// Is the workplace selection complete enough to save? 'list' sectors need a
// hospital; 'text' sectors need only the sector (the name is optional).
function workplaceSelected(form) {
  const kind = window.sectorKind ? window.sectorKind(form.hospital_sector) : null;
  if (kind === 'list') return !!form.hospital_key;
  return kind === 'text';
}

function ProfileStep({ lang, form, set, onBack, onNext }) {
  // ⚠️ Gate on workplaceSelected, NOT on hospital_key: a nurse working at an
  // NGO / clinic / home / school has no hospital key and would otherwise be
  // unable to leave this step at all (real user report, 2026-08).
  const canContinue = (form.display_name || '').trim() && workplaceSelected(form);
  return (
    <>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '01 · 個人資料' : '01 · Profile'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? '介紹一下自己。' : 'A little about you.'}
      </h1>
      <div className="auth-form">
        <AuthField label={lang === 'zh' ? '顯示名稱' : 'Display name'}
          value={form.display_name} onChange={v => set('display_name', v)}
          placeholder={lang === 'zh' ? '陳大文' : 'Wing Yi Chan'} autoFocus />

        {/* Sex */}
        <div className="onb-field">
          <div className="auth-label mono">{lang === 'zh' ? '性別' : 'Sex'}</div>
          <div className="onb-roles">
            {SEX_OPTIONS.map(o => (
              <button key={o.id} type="button"
                className={'onb-chip' + (form.sex === o.id ? ' on' : '')}
                onClick={() => set('sex', o.id)}>
                {lang === 'zh' ? o.zh : o.en}
              </button>
            ))}
          </div>
        </div>

        {/* Workplace — sector toggle + hospital dropdown */}
        <HospitalPicker
          lang={lang}
          sector={form.hospital_sector}
          hospitalKey={form.hospital_key}
          orgName={form.hospital_name}
          onSector={v => set('hospital_sector', v)}
          onHospital={v => set('hospital_key', v)}
          onOrgName={v => set('hospital_name', v)}
        />

        {/* Role — plus the second-certificate affordance directly beneath it, so
            a dual-registered nurse picks BOTH roles here; the second cycle's
            dates + score are captured on the next step (SecondCycleFields). */}
        <div className="onb-field">
          <div className="auth-label mono">{lang === 'zh' ? '職位' : 'Role'}</div>
          <RolePicker lang={lang} roleEn={form.role_en} onPick={(r, def) => {
            set('role_en', r.en);
            set('role_zh', r.zh);
            if (def != null) set('required_points', def);
          }} />
          <SecondRoleField lang={lang} form={form} set={set} />
        </div>
      </div>
      <StepNav lang={lang} onBack={onBack} onNext={onNext} disabled={!canContinue} />
    </>
  );
}

// Shared role chip picker — the single source of truth for the role list in both
// the onboarding ProfileStep and Edit Profile, for BOTH the primary and the
// second certificate. Selecting a role hands back the role + its statutory
// points default (RN/EN 45/30, incl. the Psychiatric variants) via onPick;
// re-tapping the active role is a no-op so it can't wipe a manually-typed points
// value (mirrors the sector guard in HospitalPicker).
function RolePicker({ lang, roleEn, onPick }) {
  return (
    <div className="onb-roles">
      {ROLE_OPTIONS.map(r => (
        <button key={r.en} type="button"
          className={'onb-chip' + (roleEn === r.en ? ' on' : '')}
          onClick={() => { if (roleEn === r.en) return; onPick(r, ROLE_REQUIRED_POINTS[r.en]); }}>
          {lang === 'zh' ? r.zh : r.en}
        </button>
      ))}
    </div>
  );
}

// The "I hold more than one practising certificate" toggle + the second ROLE
// picker it reveals. Shared by onboarding's ProfileStep and Edit Profile so the
// two can't drift. Enabling seeds the second licence's required-points default
// from the picked role; the second cycle's DATES + score are captured on the
// next step (SecondCycleFields, onboarding) or below (Edit Profile). Collapsed +
// cleared for the single-licence majority.
function SecondRoleField({ lang, form, set }) {
  const has = !!form.has_license2;
  const disable = () => {
    set('has_license2', false);
    ['license2_role_en', 'license2_role_zh', 'license2_license_no', 'license2_cycle_start',
     'license2_renewal_date', 'license2_required_points', 'license2_baseline_points'].forEach(k => set(k, ''));
  };
  if (!has) {
    return (
      <button type="button" className="onb-disclosure" aria-expanded={false}
        onClick={() => set('has_license2', true)} style={{ marginTop: 8 }}>
        {'▸ ' + (lang === 'zh'
          ? '我持有多於一張執業證書'
          : 'I hold more than one practising certificate')}
      </button>
    );
  }
  return (
    <div style={{ marginTop: 12 }}>
      <div className="auth-label mono">{lang === 'zh' ? '第二張證書職位' : 'Second certificate role'}</div>
      <RolePicker lang={lang} roleEn={form.license2_role_en} onPick={(r, def) => {
        set('license2_role_en', r.en);
        set('license2_role_zh', r.zh);
        if (def != null) set('license2_required_points', def);
      }} />
      <button type="button" className="onb-disclosure" onClick={disable}
        style={{ color: 'var(--danger, #c0392b)', marginTop: 4 }}>
        {lang === 'zh' ? '移除第二張證書' : 'Remove second certificate'}
      </button>
    </div>
  );
}

// The second certificate's cycle capture (shown wherever the primary cycle is —
// onboarding's CycleStep and Edit Profile). Its own PC expiry (start + CNE window
// auto-derived via the shared CycleDatesField — the SAME component and therefore
// the SAME question the primary certificate asks, which is the whole point), an
// editable required-points target (seeded from the second role), the points
// already earned toward it, and an optional registration number. Renders nothing
// unless a second certificate was enabled in the role step.
function SecondCycleFields({ lang, form, set }) {
  if (!form.has_license2) return null;
  const roleLabel = lang === 'zh'
    ? (form.license2_role_zh || '第二張證書')
    : (form.license2_role_en || 'Second certificate');
  return (
    <div className="onb-field" style={{ display: 'flex', flexDirection: 'column', gap: 12,
        borderTop: '0.5px solid var(--border)', paddingTop: 14, marginTop: 6 }}>
      <div className="auth-label mono">
        {(lang === 'zh' ? '第二張證書 · ' : 'Second certificate · ') + roleLabel}
      </div>
      <CycleDatesField lang={lang} form={form} set={set}
        startKey="license2_cycle_start" endKey="license2_renewal_date" />
      <div className="auth-field">
        <div className="auth-label mono">{lang === 'zh' ? '所需總分' : 'Total points required'}</div>
        <input className="auth-input" type="number"
          value={form.license2_required_points}
          onChange={e => set('license2_required_points', e.target.value)} />
      </div>
      {/* Points-already-earned + registration number are intentionally NOT
          captured here: the second cycle's baseline defaults to 0 (like the
          primary in onboarding) and its registration number is filled in at
          export time in the NCHK preview, which seeds from license2.license_no
          and is editable there. */}
    </div>
  );
}

// Explainer shown when the user taps the "?" next to the required-points
// field. Kept as JSX (not a flat string) so the per-role minimums read as a
// list; the role labels are bolded so RN/EN stand out at a glance.
function RequiredPointsInfo({ lang }) {
  return (
    <div className="onb-info-note" role="note">
      {lang === 'zh' ? (
        <>
          <p>香港護士管理局規定，所有執業護士均須參與持續護理教育 (CNE)。強制性 CNE 政策已正式實施，護士需於每三年進修週期內累積指定學分以符合續牌要求：</p>
          <ul>
            <li><strong>註冊護士 (RN)</strong>：每三年至少 45 個 CNE 學分</li>
            <li><strong>登記護士 (EN)</strong>：每三年至少 30 個 CNE 學分</li>
          </ul>
        </>
      ) : (
        <>
          <p>The Nursing Council of Hong Kong (NCHK) requires all practising nurses to take part in Continuing Nursing Education (CNE). Under the mandatory CNE policy, nurses must accumulate the required points within each three-year cycle to renew their registration:</p>
          <ul>
            <li><strong>Registered Nurse (RN)</strong>: at least 45 CNE points every three years</li>
            <li><strong>Enrolled Nurse (EN)</strong>: at least 30 CNE points every three years</li>
          </ul>
        </>
      )}
    </div>
  );
}

// Collapsible practising-certificate card for the Edit Profile page. Collapsed,
// it shows a one-line summary (role · PC dates · required points) so a returning
// user sees their data at a glance without a wall of open fields; expanded, it
// reveals the full editors (role picker, cycle dates + auto-calc, required
// points). The *Key props let one component edit either the flat columns (the
// primary certificate) or the license2_* form keys (the second) — so the two
// certificates share identical editors and can never drift. `onRemove`, when
// given, renders a remove control (the second certificate only).
// `exempt` — this certificate is the nurse's FIRST practising certificate
// (form.first_pc, primary card only): CNE isn't required, so the points field
// reads 0 and locks. The STORED required_points is deliberately untouched —
// unticking the checkbox restores the user's value instantly, and the derived
// 0 lives in profileLicenses (data.js), the single source every consumer reads.
function LicenceCard({ lang, form, set, roleEnKey, roleZhKey, licenseNoKey, requiredKey, startKey, endKey, defaultOpen, onRemove, exempt, focusLicenseNumber }) {
  const isZh = lang === 'zh';
  const [open, setOpen] = React.useState(!!defaultOpen);
  const [infoOpen, setInfoOpen] = React.useState(false);
  const licenseInputRef = React.useRef(null);
  const roleEn = form[roleEnKey] || '';
  const roleZh = form[roleZhKey] || '';
  const roleLabel = (isZh ? roleZh : roleEn) || (isZh ? '未選擇職位' : 'No role selected');
  // Same derivation the expanded editor uses (CycleDatesField): the expiry is
  // authoritative and the start is derived from it, so a collapsed card and its
  // open state can never show a different range. The start-anchored branch is
  // the legacy fallback for a row that somehow holds a start but no expiry.
  const pcTo = form[endKey] || (form[startKey] ? pcEndFromStart(form[startKey]) : '');
  const pcFrom = form[startKey] || (pcTo ? pcStartFromEnd(pcTo) : '');
  const required = form[requiredKey];

  // A readiness deep-link opens the correct certificate and places the cursor
  // in its registration-number field. The short delay lets the expanded
  // content mount before focus/scroll runs.
  React.useEffect(() => {
    if (!focusLicenseNumber) return;
    setOpen(true);
    const timer = setTimeout(() => {
      licenseInputRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      licenseInputRef.current?.focus?.({ preventScroll: true });
    }, 80);
    return () => clearTimeout(timer);
  }, [focusLicenseNumber]);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header — tap to expand/collapse; the summary stays visible either way. */}
      <button type="button" aria-expanded={open} onClick={() => setOpen(o => !o)}
        style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 0,
          padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{roleLabel}</div>
          <div className="muted-2" style={{ fontSize: 12.5, marginTop: 3 }}>
            {`${fmtDate(pcFrom, lang) || '—'} → ${fmtDate(pcTo, lang) || '—'} · `}
            {exempt
              ? (isZh ? '0 分（暫免）' : '0 pts (exempt)')
              : `${required || '—'} ${isZh ? '分' : 'pts'}`}
          </div>
        </div>
        <span className="mono muted-2" aria-hidden="true" style={{ fontSize: 14 }}>{open ? '▾' : '▸'}</span>
      </button>

      {open ? (
        <div style={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="onb-field" style={{ margin: 0 }}>
            <div className="auth-label mono">{isZh ? '職位' : 'Role'}</div>
            <RolePicker lang={lang} roleEn={roleEn} onPick={(r, def) => {
              set(roleEnKey, r.en);
              set(roleZhKey, r.zh);
              if (def != null) set(requiredKey, def);
            }} />
          </div>

          {licenseNoKey ? (
            <div>
              <AuthField
                label={isZh ? '護士管理局登記／註冊編號（選填）' : 'NCHK enrolment / registration number (optional)'}
                value={form[licenseNoKey] || ''}
                onChange={v => set(licenseNoKey, v)}
                placeholder="RNGM1234567"
                inputRef={licenseInputRef}
              />
              <div className="profile-edit-help">
                {isZh
                  ? '用於預填 NCHK 附件及查核準備度；不會公開顯示。'
                  : 'Used to pre-fill NCHK annexes and audit readiness; never shown publicly.'}
              </div>
            </div>
          ) : null}

          <CycleDatesField lang={lang} form={form} set={set} startKey={startKey} endKey={endKey} />

          <div className="auth-field">
            <div className="auth-label mono onb-label-row">
              <span>{isZh ? '所需總分' : 'Total points required'}</span>
              <button type="button" className="onb-info-btn" aria-expanded={infoOpen}
                aria-label={isZh ? 'CNE 學分要求說明' : 'About CNE point requirements'}
                onClick={() => setInfoOpen(o => !o)}>?</button>
            </div>
            <input className="auth-input" type="number"
              value={exempt ? 0 : required} disabled={!!exempt}
              onChange={e => set(requiredKey, e.target.value)} />
            {exempt ? (
              <div className="onb-info-note" role="note" style={{ marginTop: 6 }}>
                {isZh ? '首次證書暫免 — 0 分' : 'First certificate exempt — 0 pts'}
              </div>
            ) : null}
            {infoOpen ? <RequiredPointsInfo lang={lang} /> : null}
          </div>

          {onRemove ? (
            <button type="button" className="onb-disclosure" onClick={onRemove}
              style={{ color: 'var(--danger, #c0392b)' }}>
              {isZh ? '移除此執業證書' : 'Remove this certificate'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ‼️ THE ONE PLACE ANY PRACTISING CERTIFICATE'S DATES ARE CAPTURED — onboarding
// (first AND second certificate), Edit Profile (both cards) and the rollover
// sheet all render THIS component. Do not add a second date capture beside it.
//
// ‼️ AND IT ASKS FOR THE EXPIRY, NEVER THE START. Until this was unified, the
// first certificate asked "when does your certificate expire?" while the second
// asked for the start date — the same fact, requested two opposite ways on one
// screen, which reads as "these are different kinds of certificate" and gets the
// second one filled in wrong. The expiry is also the date a nurse actually knows
// off-hand (it is what the renewal reminder is about) and the date the NCHK rule
// anchors on, so it is the correct single input.
//
// The user enters ONE thing — the PC expiry — and everything else is derived
// from the NCHK rule (date math in data.js):
//   • cycle_start (PC start) = expiry + 1 day − 3 years   (stored, exports/rollover)
//   • CNE earning window     = anchored on the expiry alone (Method B, display)
// Both dates live in the existing cycle_start / renewal_date columns, so no
// schema change is needed and existing profiles are already correct — the
// derivation round-trips, so a profile captured under the old start-first flow
// reads back as standard rather than as a manual override.
//
// "Manual" mode (a non-standard certificate, e.g. a first registration whose
// first block is shorter than 3 years) is *inferred* from the data: if the
// stored start doesn't match the 3-year derivation, the "Adjust dates"
// disclosure opens and the start stops auto-tracking the expiry. No extra
// stored flag.
//
// `startKey`/`endKey` let a second practising certificate reuse this exact
// capture against different form keys, so a dual-registered nurse's two licences
// cannot drift in what they ask for or how they derive. Defaults preserve the
// primary cycle_start / renewal_date columns, keeping every caller unchanged.
// `note` appends an explainer under the card (onboarding uses it).
function CycleDatesField({ lang, form, set, startKey = 'cycle_start', endKey = 'renewal_date', note }) {
  const pcTo = form[endKey] || '';
  const computedStart = pcTo ? pcStartFromEnd(pcTo) : '';
  const pcFrom = form[startKey] || computedStart || '';
  const manual = !!(pcTo && form[startKey] && form[startKey] !== computedStart);
  const [adjust, setAdjust] = React.useState(manual);
  const win = cneWindow(pcFrom, pcTo);

  // Changing the expiry re-derives the start — unless the user has overridden it
  // for a non-standard certificate, in which case we leave their value alone.
  const onExpiry = (v) => {
    set(endKey, v);
    if (!manual) set(startKey, v ? pcStartFromEnd(v) : '');
  };

  return (
    <>
      {/* Label carries BOTH names on purpose: the certificate calls this the
          expiry, while Home and Profile call the same date the "renewal date".
          Naming it once here is what stops a user reading the two as separate
          fields — the old fix for that was a redundant "Renewal date" row in the
          card below, which now just repeats the input verbatim. */}
      <AuthField
        label={lang === 'zh' ? '執業證書到期日（續期日期）' : 'Certificate expiry (renewal date)'}
        type="date" value={pcTo} onChange={onExpiry} lang={lang} />

      <div className="onb-cycle-card">
        <div className="onb-cycle-cap mono muted-2">{lang === 'zh' ? '自動計算' : 'Auto-calculated'}</div>
        <CycleRow lang={lang} en="Certificate" zh="執業證書"
          v={`${fmtDate(pcFrom, lang) || '—'} → ${fmtDate(pcTo, lang) || '—'}`} />
        <CycleRow lang={lang} en="CNE earning window" zh="CNE 計分週期"
          v={`${fmtDate(win.start, lang) || '—'} → ${fmtDate(win.end, lang) || '—'}`} />
      </div>

      {note ? <div className="onb-info-note" role="note">{note}</div> : null}

      <button type="button" className="onb-disclosure"
        aria-expanded={adjust} onClick={() => setAdjust(a => !a)}>
        {(adjust ? '▾ ' : '▸ ') + (lang === 'zh' ? '調整日期' : 'Adjust dates')}
      </button>

      {adjust ? (
        <div className="onb-adjust">
          <AuthField
            label={lang === 'zh' ? '執業證書生效日期（開始）' : 'Certificate valid from'}
            type="date" value={pcFrom} onChange={v => set(startKey, v)} lang={lang} />
          <div className="onb-info-note" role="note">
            {lang === 'zh'
              ? '只適用於非標準執業證書（例如首次登記，首個週期不足三年）。一般情況下，開始日期會自動由到期日往前推算三年。'
              : 'Only for non-standard certificates (e.g. a first registration whose first block is shorter than three years). Normally the start date is derived as three years back from the expiry.'}
          </div>
        </div>
      ) : null}
    </>
  );
}

function CycleRow({ lang, en, zh, v }) {
  return (
    <div className="onb-cycle-row">
      <span className="mono onb-cycle-k">{lang === 'zh' ? zh : en}</span>
      <span className="onb-cycle-v">{v}</span>
    </div>
  );
}

function CycleStep({ lang, form, set, onBack, onNext, onSkipCycle, busy }) {
  // required_points is seeded by initialForm and updated only when the user
  // switches role on the profile step — never re-derived here, so a manual
  // edit on this step survives navigating back and forth (CycleStep unmounts
  // on Back, so any mount-time effect would silently clobber that edit).
  const [infoOpen, setInfoOpen] = React.useState(false);

  return (
    <>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '02 · 註冊週期' : '02 · Registration cycle'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? '你的續期日期。' : 'Your renewal date.'}
      </h1>
      <p className="auth-sub">
        {lang === 'zh'
          ? '只需輸入執業證書的到期日，我們會按規則自動計算 CNE 計分週期。'
          : 'Enter the expiry on your practising certificate and we’ll calculate the CNE earning window.'}
      </p>
      <div className="auth-form">
        {/* Same capture the second certificate below and Edit Profile use —
            one component, one question ("when does it expire?"), so no two
            certificates on this screen can ask for opposite ends of the same
            date range. */}
        <CycleDatesField lang={lang} form={form} set={set}
          note={lang === 'zh'
            ? '請以執業證書上的到期日為準。我們會按 NCHK 規則推算 CNE 計分週期，你之後亦可在「我的」修改。'
            : 'Use the expiry printed on your certificate. We derive the CNE earning window using the NCHK rule; you can edit it later in Profile.'} />

        {/* First-PC exemption — a first-time applicant's first certificate is CNE-
            exempt (NCHK). Flips the mandatory framing off via first_pc; the cycle
            dates still show for voluntary tracking. */}
        <label className="onb-firstpc" style={{ display: 'flex', gap: 10, alignItems: 'flex-start',
            marginTop: 2, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.first_pc}
            onChange={e => set('first_pc', e.target.checked)}
            style={{ marginTop: 3, flexShrink: 0, width: 16, height: 16, accentColor: 'var(--primary)' }} />
          <span style={{ fontSize: 13.5, lineHeight: 1.45, color: 'var(--fg-2)' }}>
            {lang === 'zh'
              ? '這是我的第一張執業證書（首次申請 — 首個證書暫免 CNE 要求）'
              : 'This is my first practising certificate (first-time applicant — CNE not required for the first certificate)'}
          </span>
        </label>

        {/* Total points required — with a "?" that reveals the NCHK rule.
            First-PC exempt: reads 0 and locks (mirrors LicenceCard); the stored
            value stays intact so unticking restores it. */}
        <div className="auth-field">
          <div className="auth-label mono onb-label-row">
            <span>{lang === 'zh' ? '所需總分' : 'Total points required'}</span>
            <button type="button" className="onb-info-btn"
              aria-expanded={infoOpen}
              aria-label={lang === 'zh' ? 'CNE 學分要求說明' : 'About CNE point requirements'}
              onClick={() => setInfoOpen(o => !o)}>?</button>
          </div>
          <input className="auth-input" type="number"
            value={form.first_pc ? 0 : form.required_points} disabled={!!form.first_pc}
            onChange={e => set('required_points', e.target.value)} />
          {form.first_pc ? (
            <div className="onb-info-note" role="note" style={{ marginTop: 6 }}>
              {lang === 'zh' ? '首次證書暫免 — 0 分' : 'First certificate exempt — 0 pts'}
            </div>
          ) : null}
          {infoOpen ? <RequiredPointsInfo lang={lang} /> : null}
        </div>

        {/* Second certificate's cycle + score — shown only when the nurse enabled
            a second role on the profile step (SecondRoleField). */}
        <SecondCycleFields lang={lang} form={form} set={set} />

        {/* Where did you find us */}
        <div className="onb-field">
          <div className="auth-label mono">{lang === 'zh' ? '你從何得知我們?' : 'Where did you find us?'}</div>
          <div className="onb-roles">
            {REFERRAL_OPTIONS.map(o => (
              <button key={o.id} type="button"
                className={'onb-chip' + (form.referral_source === o.id ? ' on' : '')}
                onClick={() => set('referral_source', o.id)}>
                {lang === 'zh' ? o.zh : o.en}
              </button>
            ))}
          </div>
        </div>
      </div>
      <StepNav lang={lang} onBack={onBack} onNext={onNext}
        disabled={!form.renewal_date || busy} busy={busy} />
      <button type="button" className="auth-link" disabled={busy}
        onClick={onSkipCycle} style={{ width: '100%', marginTop: 12 }}>
        {lang === 'zh' ? '我不確定，稍後再填' : "I'm not sure — fill this in later"}
      </button>
    </>
  );
}

function PointsStep({ lang, form, set, onBack, onNext }) {
  const pct = Math.min(100, Math.round((Number(form.baseline_points) || 0) / (Number(form.required_points) || 45) * 100));
  return (
    <>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '03 · 已有積分' : '03 · Baseline'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? '你已經累積了多少？' : 'Where are you so far?'}
      </h1>
      <p className="auth-sub">
        {lang === 'zh'
          ? '如未確定，可填 0，稍後再從紀錄補上。'
          : 'Not sure? Leave it at 0 — you can log each course later in Record.'}
      </p>
      <div className="onb-points-card">
        <div className="serif onb-points-num">{form.baseline_points || 0}</div>
        <div className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          / {form.required_points} {lang === 'zh' ? '分' : 'pts'}
        </div>
        <div className="onb-points-bar"><div className="onb-points-fill" style={{ width: `${pct}%` }} /></div>
      </div>
      <div className="auth-form">
        <AuthField label={lang === 'zh' ? '已取得 CNE 分' : 'CNE points already earned'} type="number"
          value={form.baseline_points} onChange={v => set('baseline_points', v)} autoFocus />
      </div>
      <StepNav lang={lang} onBack={onBack} onNext={onNext} />
    </>
  );
}

function DoneStep({ lang, form, busy, onBack, onFinish }) {
  // Summarise the workplace through the same resolver the Profile screen uses,
  // fed the columns this form is about to persist — so the review step shows
  // exactly what gets stored, hospital or free-text organisation alike.
  const wp = window.resolveWorkplace
    ? window.resolveWorkplace(workplacePatch(form))
    : { en: '', zh: '' };
  const hospLabel = (lang === 'zh' ? wp.zh : wp.en) || '—';
  // DoneStep renders before finish() persists the profile, so build the same
  // licence descriptors the Home wallet uses directly from the form. Records
  // and baseline are both honestly zero for a newly created account.
  const syntheticProfile = {
    role_en: form.role_en,
    role_zh: form.role_zh,
    cycle_start: form.cycle_start || null,
    renewal_date: form.renewal_date || null,
    required_points: Number(form.required_points),
    baseline_points: 0,
    first_pc: !!form.first_pc,
    license2: license2FromForm(form),
  };
  const today = window.todayIso ? window.todayIso() : new Date().toISOString().slice(0, 10);
  const previews = (window.profileLicenses ? window.profileLicenses(syntheticProfile) : [])
    .map(lic => ({ lic, progress: window.licenseProgress([], lic, today) }));
  return (
    <>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '完成' : 'Ready'}</div>
      <h1 className="auth-title serif">
        {lang === 'zh' ? '你的 CNE 計劃已準備好。' : 'Your CNE plan is ready.'}
      </h1>
      <p className="auth-sub">
        {lang === 'zh'
          ? '加入第一張證書後，這裡的進度便會立即更新。'
          : 'Add your first certificate and this progress will update straight away.'}
      </p>

      <div className="card" style={{ padding: '14px 15px', display: 'grid', gap: 12 }}>
        <div className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
          {lang === 'zh' ? '你的 CNE 概覽' : 'Your CNE overview'}
        </div>
        {previews.map(({ lic, progress }, i) => {
          const cnePeriod = progress.window || {};
          const hasDates = !!(cnePeriod.start && cnePeriod.end);
          // Do not render NaN days if an incomplete/old bundle lacks the
          // shared helper. Normal builds always use the exact Home calculation.
          const daysRemaining = hasDates && window.daysBetween
            ? window.daysBetween(today, cnePeriod.end) : null;
          return (
            <div key={lic.key} style={{ display: 'grid', gap: 7, paddingTop: i ? 12 : 0,
              borderTop: i ? '1px solid var(--border)' : undefined }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {lang === 'zh' ? lic.role.zh : lic.role.en}
              </div>
              {lic.exempt && progress.required === 0 ? (
                <div style={{ color: 'var(--accent)', fontSize: 13.5, fontWeight: 650 }}>
                  {lang === 'zh' ? '首次執業證書暫免 CNE' : 'CNE exempt for your first practising certificate'}
                </div>
              ) : (
                <>
                  <SumRow lang={lang} en="CNE target" zh="CNE 目標" v={`${progress.required} ${lang === 'zh' ? '分' : 'pts'}`} />
                  <SumRow lang={lang} en="Current" zh="目前" v={`${progress.earned} ${lang === 'zh' ? '分' : 'pts'}`} />
                </>
              )}
              {hasDates ? (
                <>
                  <SumRow lang={lang} en="CNE period" zh="CNE 計分期" v={`${cnePeriod.start} → ${cnePeriod.end}`} />
                  <SumRow lang={lang} en="Days remaining" zh="剩餘日數"
                    v={daysRemaining == null ? '—' : `${daysRemaining} ${lang === 'zh' ? '日' : 'days'}`} />
                  <SumRow lang={lang} en="Renewal date" zh="續期日" v={lic.renewalDate || '—'} />
                </>
              ) : (
                <div className="muted" style={{ fontSize: 13 }}>
                  {lang === 'zh' ? 'CNE 日期將在填妥執業證書日期後顯示。' : 'Your CNE dates will appear once certificate dates are set.'}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="onb-summary" style={{ marginTop: 12 }}>
        <SumRow lang={lang} en="Name" zh="顯示名稱" v={form.display_name || '—'} />
        <SumRow lang={lang} en="Workplace" zh="工作單位" v={hospLabel} />
        <SumRow lang={lang} en="Role" zh="職位" v={lang === 'zh' ? form.role_zh : form.role_en} />
      </div>
      <StepNav lang={lang} onBack={onBack} onNext={onFinish}
        nextLabel={lang === 'zh' ? '下一步' : 'Next'} />
    </>
  );
}

// Final step. TWO selectable cards plus a low-emphasis text link — deliberately
// NOT three equal cards.
//
// ‼️ This is the completion step, and the card that is selected on arrival is
// doing real work: a nurse who just taps 完成設定 gets routed into the AI scan.
// Three equal-weight options at the finish line would dissolve that default and
// turn "finish setup" into a decision. So the two paths that lead somewhere are
// cards, and the one that leads nowhere ('later') is a text link.
//
// 'browse' exists because 稍後再說 used to be the ONLY alternative and it is a
// dead end — its whole payoff is a Home reminder. Plenty of nurses sign up with
// no certificate to hand; Discover → 報名 → 進行中 is a real activation path
// that needs no certificate at all.
function FirstActionStep({ lang, firstAction, setFirstAction, busy, onBack, onFinish }) {
  const Choice = ({ id, title, sub }) => {
    const on = firstAction === id;
    return (
      <button type="button" role="radio" aria-checked={on}
        className={'card ' + (on ? 'is-selected' : '')}
        onClick={() => setFirstAction(id)}
        style={{ textAlign: 'left', border: on ? '1px solid var(--primary)' : undefined, cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
        <div style={{ fontWeight: 700 }}>{title}</div>
        <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>{sub}</div>
      </button>
    );
  };
  return (
    <>
      <div className="auth-eyebrow mono">{lang === 'zh' ? '第一個紀錄' : 'First record'}</div>
      <h1 className="auth-title serif">{lang === 'zh' ? '立即看到第一個分數。' : 'See your first point now.'}</h1>
      <p className="auth-sub">
        {lang === 'zh'
          ? '掃描證書、課程海報或課程網址，AI 會幫你填寫紀錄；儲存前你可逐項核對。'
          : 'Scan a certificate or course poster, or paste a course link. AI prefills the record for you to check before saving.'}
      </p>
      <div style={{ display: 'grid', gap: 10 }} role="radiogroup"
        aria-label={lang === 'zh' ? '完成設定後要做甚麼' : 'What to do after setup'}>
        <Choice id="record"
          title={lang === 'zh' ? '立即記錄第一個課程' : 'Record my first course'}
          sub={lang === 'zh'
            ? '證書、海報或課程網址都可以，約一分鐘。已完成或啱報咗名嘅課程都得。'
            : 'Certificate, poster or course link — about a minute. Works for finished and just-enrolled courses.'} />
        <Choice id="browse"
          title={lang === 'zh' ? '先看看有咩課程' : 'Browse courses first'}
          sub={lang === 'zh'
            ? '睇下最近嘅 CNE 課程，報咗名會自動記入「進行中」。'
            : 'See what’s on. Anything you enrol in lands in “In progress”.'} />
      </div>
      {/* 'later' is reachable but never pre-selected, and never a card: it is
          the absence of a first action, not a third thing to choose. */}
      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <button type="button" className="auth-link"
          aria-pressed={firstAction === 'later'}
          onClick={() => setFirstAction('later')}
          style={{
            // Muted until chosen, then it takes .auth-link's primary colour —
            // the only two properties that should differ from the house style.
            color: firstAction === 'later' ? undefined : 'var(--fg-3)',
            fontWeight: firstAction === 'later' ? 700 : undefined,
          }}>
          {lang === 'zh' ? '稍後再說（首頁會保留提示）' : 'I’ll do this later (a reminder stays on Home)'}
        </button>
      </div>
      <StepNav lang={lang} onBack={onBack} onNext={() => onFinish(false)}
        nextLabel={busy ? (lang === 'zh' ? '儲存中…' : 'Saving…') : (lang === 'zh' ? '完成設定' : 'Finish setup')}
        busy={busy} disabled={busy} />
    </>
  );
}
function SumRow({ lang, en, zh, v }) {
  return (
    <div className="onb-sum-row">
      <span className="mono muted-2" style={{ fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
        {lang === 'zh' ? zh : en}
      </span>
      <span style={{ fontSize: 13.5 }}>{v}</span>
    </div>
  );
}

// The production bundle concatenates these files into one scope. Expose the
// shared profile primitives as well so preview.html's per-file Babel transform
// exercises the same Edit Profile implementation instead of failing on an
// otherwise bundle-private identifier.
Object.assign(window, {
  OnboardingFlow,
  initialForm,
  license2FromForm,
  workplaceSelected,
  workplacePatch,
  SEX_OPTIONS,
  HospitalPicker,
  LicenceCard,
  CycleDatesField,
});

;Object.assign(typeof window !== 'undefined' ? window : globalThis, { providerDisplayName, ROLE_REQUIRED_POINTS, initialForm, license2FromForm, OnboardingFlow, StepNav, Welcome, OnbBullet, SEX_OPTIONS, REFERRAL_OPTIONS, ROLE_OPTIONS, HospitalPicker, workplacePatch, workplaceSelected, ProfileStep, RolePicker, SecondRoleField, SecondCycleFields, RequiredPointsInfo, LicenceCard, CycleDatesField, CycleRow, CycleStep, PointsStep, DoneStep, FirstActionStep, SumRow });
